import { ReactNode, useEffect, useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { BottomSheet } from "UIPalette/BottomSheet/BottomSheet";
import { ExperienceFeedback } from "UIPalette/ExperienceModal/ExperienceFeedback";
import Button from "shared/UI/Buttons/Button";
import { Chip } from "shared/UI/Chip/Chip";
import { Modal } from "shared/UI/Modal/Modal";
import { useMatchMedia } from "shared/UI/Hooks/useMatchMedia";
import { ResponsiveBreakpoints } from "shared/UI/ResponsiveBreakpoints";
import { ReactComponent as CrossIcon } from "shared/assets/Icons/cross.svg";
import WisdomAIImage from "shared/assets/wisdom-ai.svg";
import { useMobilePlatform } from "core/hooks/useMobilePlatform";
import { EventNames } from "core/monitoring/types/enums";
import { trackEvents } from "core/monitoring/Events";

type WisdomAIComingSoonProps = {
  isOpen: boolean;
  onClose: () => void;
};

type ComingSoonContent = {
  title?: string;
  header: ReactNode;
  footer?: ReactNode;
  body: ReactNode;
};

export const WisdomAIComingSoon = ({ isOpen, onClose }: WisdomAIComingSoonProps) => {
  const { t } = useTranslation(undefined, { keyPrefix: "wisdomAI.comingSoon" });
  const { t: tRoot } = useTranslation();
  const { isMobileApp } = useMobilePlatform();
  const isDesktop = useMatchMedia(ResponsiveBreakpoints.Desktop) && !isMobileApp;
  const titleId = useId();
  const [hasResponded, setHasResponded] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setHasResponded(false);
    }
  }, [isOpen]);

  const handleDismiss = () => {
    if (!hasResponded) {
      trackEvents(EventNames.WISDOM_AI_COMING_SOON_DISMISSED);
    }
    onClose();
  };

  const handleResponse = (eventName: EventNames) => {
    trackEvents(eventName);
    setHasResponded(true);
  };

  const content: ComingSoonContent = hasResponded
    ? {
        title: t("confirmationTitle"),
        header: <img src={WisdomAIImage} alt="" width={253} height={125} data-testid="wisdom-ai-confirmation-image" />,
        footer: <Button text={t("finish")} onClick={handleDismiss} />,
        body: <p className="text-center">{t("confirmationDescription")}</p>,
      }
    : {
        header: <Chip text={t("badge")} theme="purple" boldText />,
        body: (
          <div className="space-y-6 text-center">
            <p>{t("description")}</p>
            <ExperienceFeedback
              message={t("question")}
              positiveLabel={t("positive")}
              negativeLabel={t("negative")}
              onPositive={() => handleResponse(EventNames.WISDOM_AI_COMING_SOON_INTERESTED)}
              onNegative={() => handleResponse(EventNames.WISDOM_AI_COMING_SOON_NOT_INTERESTED)}
            />
          </div>
        ),
      };

  if (isDesktop) {
    if (!isOpen) {
      return null;
    }
    return (
      <Modal width="xs" close={handleDismiss}>
        <div role="dialog" aria-modal="true" aria-labelledby={content.title ? titleId : undefined} className="relative px-6 pb-6 pt-12">
          <button
            type="button"
            aria-label={tRoot("modals.dismiss")}
            onClick={handleDismiss}
            className="absolute right-4 top-4 z-10 h-4 w-4"
          >
            <CrossIcon className="fill-brand-grey hover:fill-brand-grey-light" />
          </button>
          <div className="mb-4 flex justify-center">{content.header}</div>
          {content.title && (
            <h2 id={titleId} className="mb-4 text-center">
              {content.title}
            </h2>
          )}
          {content.body}
          {content.footer && <div className="mt-8 space-y-2">{content.footer}</div>}
        </div>
      </Modal>
    );
  }

  return (
    <BottomSheet isOpen={isOpen} title={content.title} header={content.header} footer={content.footer} onClose={handleDismiss}>
      {content.body}
    </BottomSheet>
  );
};
